import { Suspense, Component, useEffect, type ReactNode, type ErrorInfo } from 'react'
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { usePageTracking } from '@/hooks/usePageTracking'
import { usePageHead } from '@/hooks/usePageHead'
import { NOT_FOUND_HEAD } from '@/lib/seo'
import { Loader2 } from 'lucide-react'
import type { Session } from '@supabase/supabase-js'
import Landing from '@/pages/Landing'
import Auth from '@/pages/Auth'
import AuthCallback from '@/pages/AuthCallback'
import Quiz from '@/pages/Quiz'
import Games from '@/pages/Games'
import Review from '@/pages/Review'
import Dashboard from '@/pages/Dashboard'
import Flashcards from '@/pages/Flashcards'
import Sidebar from '@/components/Sidebar'
import SoundEffects from '@/components/SoundEffects'
import MathFocus from '@/components/MathFocus'
import PaperRouter from '@/components/PaperRouter'
import ImageFocus from '@/components/ImageFocus'
import PdfReaderHost from '@/components/PdfReaderHost'
import FlashcardSync from '@/components/FlashcardSync'
import AnalyticsTracker from '@/components/AnalyticsTracker'
import Toast from '@/components/Toast'
import { ResumeDock } from '@/components/ResumeDock'
import { TutorHost } from '@/components/tutor/TutorHost'
import { AuthProvider } from '@/contexts/AuthContext'
import { ExamProgressProvider } from '@/contexts/ExamProgressContext'
import { useAuth } from '@/hooks/useAuth'
import { useSubscription } from '@/hooks/useSubscription'
import { ProCelebration } from '@/components/ProCelebration'
import { canEnterMode, modeDestination, type AppMode } from '@/lib/appMode'
import { actuariaDestination } from '@/lib/actuaria/access'
import { useActuariaAccess } from '@/hooks/useActuariaAccess'
import { useCommunityAccess } from '@/hooks/useCommunityAccess'
import { ACTUARIA_ENABLED, COMMUNITY_ENABLED, COWORK_ENABLED, COWORK_OPEN_TO_ALL, RESEARCH_TAB_ENABLED, TOOLS_ENABLED, TOUR_ENABLED } from '@/lib/featureFlags'
import { pageHostsNavButton } from '@/lib/mobileNavHost'
import { captureError } from '@/lib/errorMonitoring'
import { lazyRoute } from '@/lib/lazyRoute'

// Each lazy page is a `lazyRoute`, which hands back its preloader beside the
// component. `React.lazy` alone suspends on a page's first render even when
// its chunk is already here, and a page change drawn as a view transition
// would capture that frame's spinner (see lib/lazyRoute.ts).
const { Component: Research, preload: loadResearch } = lazyRoute(() => import('@/pages/Research'))
// Cowork is the app's second product (see `lib/appMode.ts`). Lazy, because a
// reader in Study mode should never pay for its catalogue.
const { Component: Cowork, preload: loadCowork } = lazyRoute(() => import('@/pages/Cowork'))
// The Projects tab — the PCPA project simulator (docs/pcpa-project.md). Lazy:
// its editor, its spreadsheet and its data generator are for the candidates
// who open it.
const { Component: Project, preload: loadProject } = lazyRoute(() => import('@/pages/Project'))
// Quiz Battle (docs/quiz-battle.md) — a game two players open on purpose, so
// its engine, its sessions and its screens wait for the click.
const { Component: Battle, preload: loadBattle } = lazyRoute(() => import('@/pages/Battle'))
// The Store (docs/store.md) — real products, bought from their sellers. Lazy:
// its catalogue, its textbook shelf and its drawings are for the readers who
// open it.
const { Component: Store, preload: loadStore } = lazyRoute(() => import('@/pages/Store'))
// Actuarial Tools (docs/actuarial-tools.md) — calculators, each one card. Lazy:
// the engine and its grids are for the readers who open a tool.
const { Component: Tools, preload: loadTools } = lazyRoute(() => import('@/pages/Tools'))
// Community (docs/community.md) — forums, events and places, by topic. Lazy,
// and approved accounts only (lib/community/access.ts).
const { Component: Community, preload: loadCommunity } = lazyRoute(() => import('@/pages/Community'))
// Workflows (docs/workflows.md) — Cowork's second place: sources, data,
// tools and concepts chained on a board. Its own chunk.
const { Component: WorkflowBuilder, preload: loadWorkflowBuilder } = lazyRoute(() => import('@/pages/WorkflowBuilder'))
// Concept Craft (docs/concept-craft.md) — a topic's concepts on a board, and
// how they connect. Lazy: its graph, its physics and the wiki bundle it reads
// the graph from are for the readers who open it.
const { Component: Craft, preload: loadCraft } = lazyRoute(() => import('@/pages/Craft'))
// Actuaria Online (docs/actuaria-online.md) — the game layer, one lazy chunk,
// so its display face, its star map and its screens never reach Study Mode.
const { Component: Actuaria, preload: loadActuaria } = lazyRoute(() => import('@/pages/Actuaria'))
// Settings, Search, the Gem Shop and the Upgrade page — opened on purpose,
// never the first screen, so they wait for the click (first-paint budget, E3).
const { Component: GemShop, preload: loadGemShop } = lazyRoute(() => import('@/pages/GemShop'))
const { Component: Settings, preload: loadSettings } = lazyRoute(() => import('@/pages/Settings'))
const { Component: Search, preload: loadSearch } = lazyRoute(() => import('@/pages/Search'))
const { Component: Upgrade, preload: loadUpgrade } = lazyRoute(() => import('@/pages/Upgrade'))
// The onboarding tour is parked (TOUR_ENABLED is off): lazy, so a parked
// feature costs first paint nothing.
const { Component: OnboardingTour } = lazyRoute(() => import('@/components/OnboardingTour'))

const { Component: WikiLayout, preload: loadWikiLayout } = lazyRoute(() => import('@/components/wiki/WikiLayout'))
const { Component: WikiHome, preload: loadWikiHome } = lazyRoute(() => import('@/pages/wiki/WikiHome'))
const { Component: WikiExam, preload: loadWikiExam } = lazyRoute(() => import('@/pages/wiki/WikiExam'))
const { Component: WikiConcept, preload: loadWikiConcept } = lazyRoute(() => import('@/pages/wiki/WikiConcept'))
const { Component: WikiResource, preload: loadWikiResource } = lazyRoute(() => import('@/pages/wiki/WikiResource'))
const { Component: WikiResources, preload: loadWikiResources } = lazyRoute(() => import('@/pages/wiki/WikiResources'))
const { Component: WikiQuestion, preload: loadWikiQuestion } = lazyRoute(() => import('@/pages/wiki/WikiQuestion'))

/**
 * Warm the chunks a path needs before navigating to it, or null when it needs
 * none. `PaperRouter` waits on this: a view transition snapshots the page as
 * soon as the route has rendered, so flushing straight into a lazy route would
 * slide its Suspense spinner in instead of the page. Returning null is the
 * common case — every eagerly imported page.
 */
function preloadRoute(path: string): Promise<unknown> | null {
  const route = path.split('?')[0].split('#')[0]
  if (route === '/research' || route.startsWith('/research/')) return loadResearch()
  if (TOOLS_ENABLED && (route === '/cowork/workflows' || route.startsWith('/cowork/workflows/'))) return loadWorkflowBuilder()
  if (route === '/cowork' || route.startsWith('/cowork/')) return loadCowork()
  if (route === '/project' || route.startsWith('/project/')) return loadProject()
  if (route === '/battle') return loadBattle()
  if (route === '/craft' || route.startsWith('/craft/')) return loadCraft()
  if (route === '/store') return loadStore()
  if (route === '/store/gems') return loadGemShop()
  if (route === '/upgrade') return loadUpgrade()
  if (route === '/settings') return loadSettings()
  if (route === '/search') return loadSearch()
  if (COMMUNITY_ENABLED && (route === '/community' || route.startsWith('/community/'))) return loadCommunity()
  if (TOOLS_ENABLED && (route === '/tools' || route.startsWith('/tools/'))) return loadTools()
  if (ACTUARIA_ENABLED && (route === '/actuaria' || route.startsWith('/actuaria/'))) return loadActuaria()
  if (route === '/wiki') return Promise.all([loadWikiLayout(), loadWikiHome()])
  if (route === '/wiki/resources') return Promise.all([loadWikiLayout(), loadWikiResources()])
  if (route.startsWith('/wiki/exam/')) return Promise.all([loadWikiLayout(), loadWikiExam()])
  if (route.startsWith('/wiki/concept/')) return Promise.all([loadWikiLayout(), loadWikiConcept()])
  if (route.startsWith('/wiki/resource/')) return Promise.all([loadWikiLayout(), loadWikiResource()])
  if (route.startsWith('/wiki/question/')) return Promise.all([loadWikiLayout(), loadWikiQuestion()])
  return null
}

/**
 * Study Guides is one tap from every page, and its chunk carries the whole
 * wiki bundle — the slowest route to open cold. Once the app is idle, fetch
 * it, so the first switch to it moves at once instead of holding the old page
 * on screen while it downloads. Skipped for a reader who has asked the browser
 * to save data.
 */
function WarmStudyGuides() {
  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (connection?.saveData) return
    const warm = () => { preloadRoute('/wiki')?.catch(() => { /* fetched again on the click */ }) }
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(warm, { timeout: 5000 })
      return () => window.cancelIdleCallback(id)
    }
    const timer = window.setTimeout(warm, 3000)
    return () => window.clearTimeout(timer)
  }, [])
  return null
}

function WikiFallback() {
  return (
    <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>
  )
}

interface ErrorBoundaryState { error: Error | null }

interface ErrorBoundaryProps {
  children: ReactNode
  // When provided, this is rendered instead of the full-page crash screen. Used
  // for app-level portals that live outside a route
  // boundary — a crash there would otherwise unmount the whole tree and leave a
  // blank screen. `null` degrades gracefully by simply removing the failed UI.
  fallback?: ReactNode
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError(error, { source: 'error-boundary', fatal: true, componentStack: info.componentStack ?? undefined })
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback
      return (
        <div className="container max-w-2xl mx-auto px-4 py-16 space-y-4">
          <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            The dashboard crashed with the following error. Please report this to the team.
          </p>
          <pre className="text-xs bg-muted rounded p-4 overflow-auto whitespace-pre-wrap break-all">
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function NotFound() {
  usePageHead(NOT_FOUND_HEAD)
  return (
    <div className="container max-w-md mx-auto px-4 py-16 text-center space-y-4">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground">Page not found</p>
      <Link to="/" className="text-primary hover:underline text-sm">
        ← Back to Home
      </Link>
    </div>
  )
}

function PageTracker() {
  usePageTracking()
  return null
}

function GlobalKeyHandler() {
  const navigate = useNavigate()
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        const target = e.target as HTMLElement | null
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
        e.preventDefault()
        navigate('/search')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate])
  return null
}

// Below lg the app header is fixed, so the content reserves its height — but
// only on the routes that get one. A route whose own top bar carries the nav
// button has no header above it and starts at the top of the viewport; the
// decision is `lib/mobileNavHost.ts`, read here and in `Sidebar.tsx` so the two
// can't disagree about whether that row exists.
function Main({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return (
    <main className={`flex-1 min-w-0 lg:pt-0 ${pageHostsNavButton(pathname) ? '' : 'pt-14'}`}>
      {children}
    </main>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/auth" replace />
  return <>{children}</>
}


/**
 * A route that belongs to a gated mode. The rule is the mode's own
 * (`canEnterMode` in `lib/appMode.ts`), and a viewer it keeps out goes where
 * `modeDestination` sends them — the same place the mode pill would — so the
 * pill and the URL can never disagree about who gets in or where a locked
 * mode leads. For a Preview mode that means an account that isn't approved
 * lands back on the dashboard, as if the route didn't exist.
 *
 * `loading` matters: subscription state resolves asynchronously, and bouncing
 * an entitled viewer away for the frame before their row arrives is a bug
 * they would see every time they open the app.
 */
function RequireMode({ mode, children }: { mode: AppMode; children: ReactNode }) {
  const { user } = useAuth()
  const { isPro, loading } = useSubscription()
  const viewer = { signedIn: !!user, isPro, email: user?.email, previewOpen: COWORK_OPEN_TO_ALL }
  if (canEnterMode(mode, viewer)) return <>{children}</>
  if (user && loading) return <WikiFallback />
  return <Navigate to={modeDestination(mode, viewer)} replace />
}

function CoworkRoute() {
  if (!COWORK_ENABLED) return <Navigate to="/dashboard" replace />
  return (
    <RequireMode mode="cowork">
      <ErrorBoundary>
        <Suspense fallback={<WikiFallback />}>
          <Cowork />
        </Suspense>
      </ErrorBoundary>
    </RequireMode>
  )
}

function BattleRoute() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Battle />
      </Suspense>
    </ErrorBoundary>
  )
}

function CraftRoute() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Craft />
      </Suspense>
    </ErrorBoundary>
  )
}

function ActuariaRoute() {
  const location = useLocation()
  const { allowed, viewer } = useActuariaAccess()
  if (!ACTUARIA_ENABLED) return <Navigate to="/wiki" replace />
  // Approved accounts only (lib/actuaria/access.ts): send anyone else to sign
  // in, or — signed in and not approved — back to the dashboard.
  if (!allowed) {
    return <Navigate to={actuariaDestination(viewer)} replace state={{ from: location.pathname + location.search }} />
  }
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Actuaria />
      </Suspense>
    </ErrorBoundary>
  )
}

function StoreRoute() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Store />
      </Suspense>
    </ErrorBoundary>
  )
}

function CommunityRoute() {
  const allowed = useCommunityAccess()
  if (!allowed) return <Navigate to="/dashboard" replace />
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Community />
      </Suspense>
    </ErrorBoundary>
  )
}

function ToolsRoute() {
  if (!TOOLS_ENABLED) return <Navigate to="/dashboard" replace />
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Tools />
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * Workflows — Cowork's second place, so behind Cowork's own gate
 * (`RequireMode`), and run on the Actuarial Tools engine, so behind its flag
 * too.
 */
function WorkflowRoute() {
  if (!COWORK_ENABLED || !TOOLS_ENABLED) return <Navigate to="/dashboard" replace />
  return (
    <RequireMode mode="cowork">
      <ErrorBoundary>
        <Suspense fallback={<WikiFallback />}>
          <WorkflowBuilder />
        </Suspense>
      </ErrorBoundary>
    </RequireMode>
  )
}

function ProjectRoute() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<WikiFallback />}>
        <Project />
      </Suspense>
    </ErrorBoundary>
  )
}

export default function App({ initialSession }: { initialSession: Session | null }) {
  return (
    // Every change of page slides like paper on a desk rather than cutting —
    // see components/PaperRouter.tsx and lib/viewTransition.ts.
    <PaperRouter preload={preloadRoute}>
      <PageTracker />
      <WarmStudyGuides />
      <GlobalKeyHandler />
      <SoundEffects />
      <AuthProvider initialSession={initialSession}>
        <FlashcardSync />
        <ExamProgressProvider>
          <AnalyticsTracker />
          <div className="min-h-screen bg-background text-foreground flex">
            <Sidebar />
            <Main>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/games" element={<Games />} />
                <Route path="/battle" element={<BattleRoute />} />
                {/* Concept Craft: the board, and a link adding one topic's pack to it. */}
                <Route path="/craft" element={<CraftRoute />} />
                <Route path="/craft/:topicId" element={<CraftRoute />} />
                {/* Actuaria Online: the title, the star map, the sectors, the
                    Daily Transmission, the Hangar and Monte Carlo Station —
                    its own routes, inside its own scope (pages/Actuaria). */}
                <Route path="/actuaria/*" element={<ActuariaRoute />} />
                <Route path="/review" element={<Review />} />
                <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                <Route path="/search" element={<Suspense fallback={<WikiFallback />}><Search /></Suspense>} />
                <Route path="/flashcards" element={<ErrorBoundary><Flashcards /></ErrorBoundary>} />
                <Route path="/browse" element={<Navigate to="/search" replace />} />
                <Route path="/settings" element={<Suspense fallback={<WikiFallback />}><Settings /></Suspense>} />
                <Route path="/upgrade" element={<Suspense fallback={<WikiFallback />}><Upgrade /></Suspense>} />
                {/* The Store: real products, each bought on its seller's own
                    page. The gem shop is a corner of it. */}
                <Route path="/store" element={<StoreRoute />} />
                <Route path="/tools" element={<ToolsRoute />} />
                {/* Workflows moved to Cowork; an old link lands there. */}
                <Route path="/tools/workflow" element={<Navigate to="/cowork/workflows" replace />} />
                <Route path="/tools/:slug" element={<ToolsRoute />} />
                <Route path="/community" element={<CommunityRoute />} />
                <Route path="/community/topic/:topicId" element={<CommunityRoute />} />
                <Route path="/store/gems" element={<ErrorBoundary><Suspense fallback={<WikiFallback />}><GemShop /></Suspense></ErrorBoundary>} />
                <Route path="/research" element={
                  RESEARCH_TAB_ENABLED ? (
                    <RequireAuth>
                      <ErrorBoundary>
                        <Suspense fallback={<WikiFallback />}>
                          <Research />
                        </Suspense>
                      </ErrorBoundary>
                    </RequireAuth>
                  ) : (
                    <Navigate to="/wiki" replace />
                  )
                } />
                {/* Cowork — in Preview, open to approved accounts only. The
                    Sources paths are one page: the open source lives in the
                    URL so it can be linked to and Back walks the loop.
                    Workflows is its own full-screen board. `RequireMode` is
                    what keeps the mode's own rule (`canEnterMode`) true of the
                    route and not only of the pill. Deliverables were retired
                    for Workflows; an old link lands there. */}
                <Route path="/cowork/workflows" element={<WorkflowRoute />} />
                <Route path="/cowork/deliverables" element={<Navigate to="/cowork/workflows" replace />} />
                <Route path="/cowork/deliverables/:id" element={<Navigate to="/cowork/workflows" replace />} />
                <Route path="/cowork" element={<CoworkRoute />} />
                <Route path="/cowork/:tab" element={<CoworkRoute />} />
                <Route path="/cowork/:tab/:id" element={<CoworkRoute />} />
                {/* The Projects tab: the briefs, and one attempt. `/project/pcpa`
                    was the PCPA portal before projects were a tab of their own. */}
                <Route path="/project" element={<ProjectRoute />} />
                <Route path="/project/pcpa" element={<Navigate to="/project" replace />} />
                <Route path="/project/pcpa/:attemptId" element={<ProjectRoute />} />
                <Route path="/wiki" element={
                  <Suspense fallback={<WikiFallback />}>
                    <WikiLayout><WikiHome /></WikiLayout>
                  </Suspense>
                } />
                {/* The Study Guides tab's second page: every resource, filtered
                    by exam, publisher and year. */}
                <Route path="/wiki/resources" element={
                  <Suspense fallback={<WikiFallback />}>
                    <WikiLayout><WikiResources /></WikiLayout>
                  </Suspense>
                } />
                <Route path="/wiki/exam/:slug" element={
                  <Suspense fallback={<WikiFallback />}>
                    <WikiLayout><WikiExam /></WikiLayout>
                  </Suspense>
                } />
                <Route path="/wiki/concept/:slug" element={
                  <Suspense fallback={<WikiFallback />}>
                    <WikiLayout><WikiConcept /></WikiLayout>
                  </Suspense>
                } />
                <Route path="/wiki/resource/:slug" element={
                  <Suspense fallback={<WikiFallback />}>
                    <WikiLayout><WikiResource /></WikiLayout>
                  </Suspense>
                } />
                {/* One practice question, for the search that lands on it —
                    its answer is behind a click into the quiz. */}
                <Route path="/wiki/question/:id" element={
                  <Suspense fallback={<WikiFallback />}>
                    <WikiLayout><WikiQuestion /></WikiLayout>
                  </Suspense>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Main>
            {TOUR_ENABLED && <Suspense fallback={null}><OnboardingTour /></Suspense>}
            <MathFocus />
            <ImageFocus />
            {/* The welcome to Pro, whichever way it was got (lib/proCelebration.ts). */}
            <ProCelebration />
            {/* The app's one PDF reader. Root-level so a document opened from a
                dialog, a sheet or a card clears it and outlives it. */}
            <PdfReaderHost />
            {/* A quiz in progress, or a place in the battle queue, from anywhere else in the app. */}
            <ResumeDock />
            {/* The Tutor (docs/tutor.md) — the reader's own character, for the
                accounts its rollout admits; a crash in it removes the Tutor,
                never the page. */}
            <ErrorBoundary fallback={null}>
              <TutorHost />
            </ErrorBoundary>
            <Toast />
          </div>
        </ExamProgressProvider>
      </AuthProvider>
    </PaperRouter>
  )
}
